import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import { createAuthThunks } from '@adthlete/shared-frontend'
import api from '@/lib/api'

const authThunks = createAuthThunks({ api, userType: 'marketer', typePrefix: 'user' })
export const {
  login: loginThunk,
  fetchCurrentUser,
  logout: logoutThunk,
  requestMagicLink: requestMagicLinkThunk,
  verifyMagicLink: verifyMagicLinkThunk,
  confirmEmail: confirmEmailThunk,
} = authThunks

export const signupThunk = createAsyncThunk(
  'user/signup',
  async (
    userData: {
      name: string
      email: string
      password: string
      user_type: string
      org_name?: string
      org_join_code?: string
      invite_token?: string
      agreed_to_terms: boolean
      legal_version: string
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await api.post('/users/register', userData)
      return response.data
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Registration failed')
    }
  },
)

export const resendConfirmationEmailThunk = createAsyncThunk(
  'user/resendConfirmationEmail',
  async (email: string, { rejectWithValue }) => {
    try {
      const response = await api.post('/users/resend-confirmation', {
        email,
        user_type: 'marketer',
      })
      return response.data
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to resend confirmation email')
    }
  },
)

// Thunk to send verification code
export const sendPhoneCodeThunk = createAsyncThunk(
  'user/sendPhoneCode',
  async (phone: string, { rejectWithValue }) => {
    try {
      const response = await api.post('/users/phone/send-code', { phone })
      return response.data
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to send verification code')
    }
  },
)

// Thunk to verify phone code
export const verifyPhoneCodeThunk = createAsyncThunk(
  'user/verifyPhoneCode',
  async ({ phone, code }: { phone: string; code: string }, { rejectWithValue }) => {
    try {
      const response = await api.post('/users/phone/verify', { phone, code })
      return response.data
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to verify phone code')
    }
  },
)

const userSlice = createSlice({
  name: 'user',
  initialState: {
    currentUser: null, // Store user info here, e.g., { id, email }
    loading: true, // start in loading state until current user is fetched
    error: null,
  },
  reducers: {
    // Update user fields with partial data
    updateUser: (state, action: PayloadAction<any>) => {
      if (!state.currentUser) return
      state.currentUser = { ...state.currentUser, ...action.payload }
    },
    // Clear auth state when the session ends (refresh failed, or another tab logged out).
    clearUser: (state) => {
      state.currentUser = null
      state.loading = false
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loginThunk.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(loginThunk.fulfilled, (state, action) => {
        state.loading = false
        state.currentUser = action.payload
      })
      .addCase(loginThunk.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      // Handle page refresh / initial load
      .addCase(fetchCurrentUser.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchCurrentUser.fulfilled, (state, action) => {
        state.loading = false
        state.currentUser = action.payload
      })
      .addCase(fetchCurrentUser.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      .addCase(logoutThunk.fulfilled, (state) => {
        state.loading = false
        state.currentUser = null // Clear user data on logout
      })
      .addCase(verifyMagicLinkThunk.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(verifyMagicLinkThunk.fulfilled, (state, action) => {
        state.loading = false
        state.currentUser = action.payload
      })
      .addCase(verifyMagicLinkThunk.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })
      .addCase(requestMagicLinkThunk.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(requestMagicLinkThunk.fulfilled, (state) => {
        state.loading = false
      })
      .addCase(requestMagicLinkThunk.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })
      .addCase(confirmEmailThunk.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(confirmEmailThunk.fulfilled, (state, action) => {
        state.loading = false
        state.currentUser = action.payload
      })
      .addCase(confirmEmailThunk.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      .addCase(verifyPhoneCodeThunk.fulfilled, (state, action) => {
        if (state.currentUser) {
          state.currentUser.phone_verified = true
          state.currentUser.phone = action.meta.arg.phone
        }
      })
  },
})

// Export actions for logout and updating user data
export const { updateUser, clearUser } = userSlice.actions

export default userSlice.reducer
